import React, { useRef, useState, useLayoutEffect } from 'react'
import { Transition } from 'react-transition-group'

import loadImage from 'image-promise'
import { gsap } from 'gsap'

import useResponsive from '../hooks/useResponsive'
import Illus from './illus'

export default (props) => {

  let canvasTl = null
  const mainIllusTl = useRef()
  const mouseContainerEl = useRef(null)
  const [isResponsiveReady, setIsResponsiveReady] = useState(false)
  const { isDesktop, isTablet, untilWidescreen } =
    useResponsive()

  // Wigglewiggle the strawberry (called from the JSX mouse event handler)
  const strawberryWiggle = (target) => {
    gsap.set(target, {
      rotation: 0,
    })
    gsap
      .from(target, {
        rotation: 45,
        duration: .8,
        ease: "elastic.out(.6, .1)",
      })
  }
    
  // Wait for all images to be loaded with the LoadImage package before
  // resuming the main animation. There is a minimum 600ms time tho.
  // (part of the gsap main animation)
  const waitForImages = () => {
    setTimeout(() => {
      loadImage(document.querySelectorAll('.illus__canvas > img'))
        .then(() => {
          mainIllusTl.current.seek('loaded').play()
        })
        .catch((err) => {
          console.log(err)
        })
    }, 600)
  }

  // Sparkling stars! (part of the gsap main animation)
  const shootingStaaar = () => {
    document.querySelectorAll('.star:not(.illus__moon)').forEach((star) => {
      star.addEventListener('mouseenter', e => {
        gsap
          .timeline({ repeat: 1, })
          .to(e.target, {
            autoAlpha: 0,
            duration: .4,
          })
          .to(e.target, {
            autoAlpha: 1,
            duration: .4,
          })
      })
    })
  }

  // Jellyfish goes zweep zwoop (part of the gsap main animation)
  const jellyfishZweepZwoop = () => {
    gsap
      .timeline({ repeat: -1, yoyo: true, ease: 'slow', })
      .fromTo('.illus__jellyfish',
        { translateY: 0, rotate: .02, },
        { translateY: 8, rotate: .02,
          duration: 4, })
  }

  // Titles are glitched by adding then removing the "title-glitch" CSS class
  // This CSS class triggers a CSS animation (part of the gsap main animation)
  const glitchMyTitle = () => {
    gsap
      .timeline({ repeat: -1, repeatDelay: 4, })
      .to('.landing__title > div:last-child > span',
        { className: 'title-glitch' })
      .to('.landing__title > div:last-child > span',
        { className: '' }, .4)

    gsap
      .timeline({ repeat: -1, repeatDelay: 1, })
      .set('.glitch-band',
        { className: 'glitch-band glitching' }, .4)
      .set('.glitch-band',
        { className: 'glitch-band' }, 3.4)
  }

  // Big glitch effects on all images wihtin illus (part of the gsap main animation)
  const canvasTurb = () => {
    const $filter = document.querySelector('.svg-sprite')
    const $turb = $filter.querySelector('#filter feTurbulence')
    const $canvas = document.querySelector('.illus__canvas')
    const turbVal = { val: 0.000001 }
    const turbValX = { val: 0.000001 }
    const $allImgs = $canvas.querySelectorAll('img')
    const $glitchers = $canvas.querySelectorAll(
      untilWidescreen
      ? '.illus__coffee, .illus__code'
      : '.illus__key, .illus__coffee, .illus__molecule, .illus__code, .illus__dragon, .illus__jellyfish'
    )

    // General glitch animation. The idea here is to animate the variables turbVal and turbValX
    // independently from the elements.
    // Then, at each update, the animated variables are applied to the svg filter (see html.js)
    // Finally, the filter is applied to the element to be animated via a CSS class
    // (Pausing and repeating are handled "manually" allowing to clean classes and randomize glitches)
    canvasTl =
      gsap
        .timeline({
          paused: true,
          onUpdate: () => {
            $turb
              .setAttribute('baseFrequency', turbVal.val + ' ' + turbValX.val);
          }
        })
        .to(turbValX, { val: 0.5, duration: .1 })
        .to(turbVal, { val: 0.02, duration: .1 })
        .set(turbValX, { val: 0.000001 })
        .set(turbVal, { val: 0.000001 })
        .to(turbValX, { val: 0.4, duration: .2 }, .4)
        .to(turbVal, { val: 0.002, duration: .2 }, .4)
        .set(turbValX, { val: 0.000001 })
        .set(turbVal, { val: 0.000001 })
        .addPause('+=0', () => {
          $allImgs.forEach(el => (el.classList.remove('glitching')))
          setTimeout(() => {
            $glitchers[Math.floor(Math.random() * $glitchers.length)].classList.add('glitching')
            canvasTl.play(0)
          }, 3000)
        })

    $allImgs.forEach(el => (el.classList.add('glitching')))
    canvasTl.play(0)
  }

  const onEnter = (node) => {

    // The big appearance timeline handling loader, titles and illus
    // This is where all animation function are called, generally after the appearance of the element

    // When precised, the animation is handled in a CSS animation (see _landing.scss)
    //   Gsap is only the trigger here, this is because translate animations are ugly on Firefox

    // useMediaQuery bools allow us to skip useless animation by setting the duration to 0

    mainIllusTl.current = gsap
      .timeline({ paused: true })

      // Show loader title slowly
      .addLabel('start')
      .to('.loader__title', {
        autoAlpha: .1,
        duration: 1,
      })

      // Pause/Resume here waiting for all images to be loaded
      .addPause('+=0', waitForImages)
      .addLabel('loaded')

      // Hide the whole loader
      .to('.loader__title', {
        autoAlpha: 0,
        duration: .8,
      })
      .to('.loader__wrapper', {
        autoAlpha: 0,
        duration: .2,
      })
      .set('html', {
        overflowY: 'scroll'
      })
      .call(() => props.setIsLoaded(true))

      // Slides up titles, then show glitch band, then all images
      .to('.landing__left-tile span', { 
        autoAlpha: 1,
        y: 0,
        duration: 0, // .3s in CSS
        stagger: .2,
      })
      .to('.glitch-band', {
        height: '3em',
        borderTopWidth: '8px',
        borderBottomWidth: '10px',
        duration: .2,
      }, "+=.2")
      .call(glitchMyTitle)

      .to('.illus__rock-big', {
        autoAlpha: 1,
        y: 0,
        duration: 0, // .6s in CSS
      }, (!untilWidescreen ? '-=.6' : '0'))

      .to('.illus__rock-arrow', {
        autoAlpha: 1,
        duration: (!untilWidescreen ? .3 : 0),
      })

      .to('.illus__me, .illus__strawberry', {
        autoAlpha: 1,
        duration: .3,
      })
    
      .to('.branch', {
        autoAlpha: 1,
        duration: .3,
      })
      .to('.animal', {
        autoAlpha: 1,
        duration: (isDesktop ? .3 : 0),
        stagger: (isDesktop ? .2 : 0),
      })
      .call(() => isDesktop && jellyfishZweepZwoop())
     
      .to('.star', {
        autoAlpha: 1,
        duration: (isDesktop ? .3 : 0),
      })
      .call(() => isDesktop && shootingStaaar())
      .call(() => isDesktop && canvasTurb())

    // Now let's play!
    mainIllusTl.current.seek('start').play()
  }

  useLayoutEffect(() => {
    setIsResponsiveReady(true)
  }, [])

  return (isResponsiveReady
    ? (<Transition
        in={true}
        appear={true}
        onEnter={onEnter}
        timeout={{
          // Yo, I don't know if this is very accurate.
          // I use the `console.log(mainIllusTl.current.totalDuration())` value.
          // That's Fing annoying to define here the timer.
          // I don't even know 'cause I'm waiting for the images to load geez.
          enter: (() => {
            if (untilWidescreen) return 3600
            if (isDesktop) return 4400
            return 4700
          })(),
        }}>
      <header className="landing hero is-fullheight" ref={mouseContainerEl}>
        <div className={"landing__body hero-body tile" + (untilWidescreen ? ' is-vertical' : '')}>
          <div className="landing__left-tile tile">
            <div className="glitch-band"></div>
            <h1
              className={"landing__title title level-item is-1"}>
              <div><span>Hello,</span></div>
              <div><span data-text="C'est moi Léo&nbsp;!">C'est moi Léo&nbsp;!</span></div>
            </h1>
            <div className={"landing__subtitle subtitle level-item is-4"}>
              <div><span data-text="Front-End Developer">Front-End Developer</span></div>
              <div><span>&amp; Creative Coder</span></div>
            </div>
          </div>
          <div className="landing__right-tile illus tile">
            {isTablet && <div className="noise"></div>}
            <Illus hoverStrawberry={strawberryWiggle} />
          </div>
        </div>
      </header>
    </Transition>)
    : null
  )
}
